import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { get } from "lodash";

const MixpanelUserTracker = () => {
    const { isAuthenticated, isLoading, user } = useAuth0();

    useEffect(() => {
        // Only proceed if we have a valid user object with required properties
        if (!isLoading && isAuthenticated && (user as any)?.sub) {
            const authUser = user as any;
            const userSub: string = String(authUser.sub);
            try {
                const mixpanel = get(window as any, "mixpanel");
                if (!mixpanel) {
                    console.error("Mixpanel not available for user identification");
                    return;
                }

                // Identify the user
                const identify = get(mixpanel, "identify");
                if (typeof identify === "function") {
                    identify.call(mixpanel, (user as any).sub);
                }

                // Set user properties if people tracking is available
                const peopleSet = get(mixpanel, "people.set");
                if (typeof peopleSet === "function") {
                    try {
                        peopleSet.call(mixpanel.people, {
                            $name: (user as any).name || "Anonymous",
                            $email: (user as any).email || "",
                            last_login: new Date().toISOString(),
                            auth0_id: (user as any).sub,
                            nickname: (user as any).nickname || "",
                            picture: (user as any).picture || "",
                            platform: "web",
                            version: "1.0.0",
                        });
                    } catch (error) {
                        console.error("Error setting Mixpanel user properties:", error);
                    }
                }

                // Persist last authenticated user for targeted cleanup on real logout
                localStorage.setItem("mp_last_user_sub", userSub);

                // Track login event only once per user until logout (localStorage-based)
                const loginTrackedKey = `mp_login_tracked_${userSub}`;
                if (!localStorage.getItem(loginTrackedKey)) {
                    const track = get(mixpanel, "track");
                    if (typeof track === "function") {
                        track.call(mixpanel, "User Login", {
                            user_id: userSub,
                            email: String(authUser.email || ""),
                            platform: "web",
                            version: "1.0.0",
                        });
                        localStorage.setItem(loginTrackedKey, "1");
                    }
                }

                // Force send any queued events
                const flush = get(mixpanel, "flush");
                if (typeof flush === "function") {
                    flush.call(mixpanel);
                }
            } catch (error) {
                console.error("Mixpanel identification error:", error);
            }
        } else if (!isLoading && !isAuthenticated) {
            try {
                const mixpanel = get(window as any, "mixpanel");
                if (!mixpanel) return;

                // Reset Mixpanel when user logs out
                const reset = get(mixpanel, "reset");
                if (typeof reset === "function") {
                    reset.call(mixpanel);
                }

                // Force send any queued events before reset
                const flush = get(mixpanel, "flush");
                if (typeof flush === "function") {
                    flush.call(mixpanel);
                }

                 // Allow login event to fire again after confirmed logout
                const lastSub = localStorage.getItem("mp_last_user_sub");
                if (lastSub) {
                    localStorage.removeItem(`mp_login_tracked_${lastSub}`);
                    localStorage.removeItem("mp_last_user_sub");
                }
            } catch (error) {
                console.error("Mixpanel reset error:", error);
            }
        }
    }, [isAuthenticated, isLoading, user]);

    return null; // This component doesn't render anything
};

export default MixpanelUserTracker;
